import React, { useState, useRef, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Bath,
  Grid3x3,
  List,
  X,
  Sparkles,
  DoorOpen,
  Zap,
  Eye,
  Tv,
  Bed,
  Menu,
  TreePine,
  ArrowLeft,
} from "lucide-react";

import HeroSection from "../components/landing/HeroSection";
import AmenitiesSection from "../components/landing/AmenitiesSection";
import AboutSection from "../components/landing/AboutSection";
import MapSection from "../components/landing/MapSection";
import ContactSection from "../components/landing/ContactSection";
import LivingConditionsSection from "../components/landing/LivingConditionsSection";
import RoomBookingDialog from "../components/landing/RoomBookingDialog";
import DesignVariantsDialog from "../components/landing/DesignVariantsDialog";
import PhotoViewer from "../components/landing/PhotoViewer";
import RoomCardHorizontal from "../components/landing/RoomCardHorizontal";
import RoomCardVertical from "../components/landing/RoomCardVertical";
// import HeaderSearchField from "../components/HeaderSearchField";
import { Button } from "../components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import ServerAPI from "../lib/server";
import { photoUrl } from "../config";
import PrintListLanding from "../components/landing/PrintListLanding";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import searchStore from "../store/searchStore";
import MainPopup from "../components/ui/MainPopup";
import BookingWindow from "../components/landing/BookingWindow";
import HotelHeaderSearchField from "../components/HotelHeaderSearchField";

const hotelDeclensions = {
  Отель: "отеле",
  "Загородный дом": "загородном доме",
  "Апарт-отель": "апарт-отеле",
  "Гостевой дом": "гостевом доме",
  Хостел: "хостеле",
  "Мини-гостиница": "мини-гостинице",
  Шале: "шале",
  Мотель: "мотеле",
  Дома: "домах",
  Капсульный: "капсульном",
  Комплекс: "комплексе",
  Дом: "доме",
  Санаторий: "санатории",
  Вилла: "вилле",
  Кемпинг: "кемпинге",
  "Люкс-шатер": "люкс-шатре",
  Апартаменты: "апартаментах",
  Виллы: "виллах",
  "База отдыха": "базе отдыха",
  "Апартаменты/квартира": "апартаментах/квартире",
  Глемпинг: "глемпинге",
};

export default function HotelSprav() {
  const { country, region, city, hotel_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Показываем ссылку «К результатам поиска» только если пришли из списка /Sprav
  const cameFromSprav = location.state?.from === "sprav";
  const cameFromMain = location.state?.from === "main";
  const handleBackToResults = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/russia");
  };
  const handleBackToMain = () => {
    navigate("/");
  };

  const [hotelData, setHotelData] = useState({});
  const [hotelImages, setHotelImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  const [roomView, setRoomView] = useState("grid");
  const [showDescription, setShowDescription] = useState(false);
  const [roomPhotoIndices, setRoomPhotoIndices] = useState({});
  const [roomSlideDirections, setRoomSlideDirections] = useState({});
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [showRules, setShowRules] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [searchedDates, setSearchedDates] = useState({
    checkIn: "",
    checkOut: "",
  });
  const [designVariantsOpen, setDesignVariantsOpen] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoViewerImages, setPhotoViewerImages] = useState([]);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(0);
  const [accentColor, setAccentColor] = useState("#14b8a6");

  // Новое состояние для объединенных комнат
  const [combinedRooms, setCombinedRooms] = useState([]);

  // Состояние для бронирования
  const [showBookingWindow, setShowBookingWindow] = useState(false);
  const [bookingData, setBookingData] = useState({
    hotel: null,
    room: null,
    tariff: null,
    food: null,
    number: null,
    datein: null,
    dateout: null,
  });

  // Установка title
  useDocumentTitle(
    hotelData?.name ? `${hotelData.name} - Гостим.рф` : "Загрузка отеля...",
  );

  // Sprav nav state
  const [mobileBurgerOpen, setMobileBurgerOpen] = useState(false);
  const mobileBurgerRef = useRef(null);

  useEffect(() => {
    const getCookie = (name) => {
      const cookies = document.cookie.split("; ");
      const cookie = cookies.find((row) => row.startsWith(`${name}=`));
      return cookie ? cookie.split("=")[1] : null;
    };

    const payload = {
      country: country || "russia",
      region: region || "",
      city: city || "",
      datein: searchStore.dateCheckin || getCookie("datein") || "",
      dateout: searchStore.dateCheckout || getCookie("dateout") || "",
      guests: searchStore.guestsCount || parseInt(getCookie("guests") || "2"),
      child:
        searchStore.guestsChildren.length > 0
          ? searchStore.guestsChildren
          : (() => {
              try {
                return JSON.parse(getCookie("childs") || "[]");
              } catch {
                return [];
              }
            })(),
      dogfriendly:
        searchStore.dogFriendly || getCookie("dogfriendly") === "true",
    };

    ServerAPI.customPostQuery(`/hotel/${hotel_id}`, payload)
      .then((res) => {
        setHotelData(res?.hotel || {});
        setLoading(false);
      })
      .catch((err) => {
        console.log("err.message", err);
        setError(err);
      });
  }, [hotel_id]);

  // Объединение rooms и price_rooms
  useEffect(() => {
    if (hotelData?.rooms || hotelData?.price_rooms) {
      const combined = [
        ...(hotelData.price_rooms || []),
        ...(hotelData.rooms || []),
      ];
      setCombinedRooms(combined);
    }
  }, [hotelData]);

  useEffect(() => {
    function handleOutside(e) {
      if (
        mobileBurgerRef.current &&
        !mobileBurgerRef.current.contains(e.target)
      )
        setMobileBurgerOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  useEffect(() => {
    setHotelImages(
      hotelData?.photos?.length > 0
        ? hotelData.photos.map((p) => ({
            src: `${photoUrl}/${hotelData.photo_url}/hotel/${p}.jpg`,
            name: p,
          }))
        : [],
    );
  }, [hotelData]);

  const [headerAdults, setHeaderAdults] = useState(2);
  const [headerChildrenAges, setHeaderChildrenAges] = useState([]);
  const [headerWithPet, setHeaderWithPet] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  const handleSearch = () => {
    if (checkIn && checkOut) {
      setSearchedDates({ checkIn, checkOut });
      searchStore.setDateCheckin(checkIn);
      searchStore.setDateCheckout(checkOut);
      scrollToSection("rooms");
    }
  };

  const calcNights = (ci, co) => {
    if (!ci || !co) return 0;
    const [y1, m1, d1] = ci.split("-").map(Number);
    const [y2, m2, d2] = co.split("-").map(Number);
    return Math.round(
      (new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000,
    );
  };

  const datesSelected = !!(searchedDates.checkIn && searchedDates.checkOut);

  const nextRoomPhoto = (roomId) => {
    setRoomSlideDirections((prev) => ({ ...prev, [roomId]: 1 }));
    setRoomPhotoIndices((prev) => {
      const room = combinedRooms.find((r) => r.id === roomId);
      return {
        ...prev,
        [roomId]: ((prev[roomId] || 0) + 1) % (room?.photos?.length || 1),
      };
    });
  };

  const prevRoomPhoto = (roomId) => {
    setRoomSlideDirections((prev) => ({ ...prev, [roomId]: -1 }));
    setRoomPhotoIndices((prev) => {
      const room = combinedRooms.find((r) => r.id === roomId);
      return {
        ...prev,
        [roomId]:
          ((prev[roomId] || 0) - 1 + (room?.photos?.length || 1)) %
          (room?.photos?.length || 1),
      };
    });
  };

  const openBookingDialog = (room) => {
    setSelectedRoom(room);
    setBookingDialogOpen(true);
  };

  const toggleBookingWindow = () => {
    setShowBookingWindow(!showBookingWindow);
  };

  const bookingHandler = (
    hotel_id_param,
    room_id,
    tariff_id,
    food_id,
    number_id = null,
    datein = null,
    dateout = null,
  ) => {
    setBookingData({
      hotel: hotel_id_param || hotelData?._id,
      room: room_id,
      tariff: tariff_id,
      food: food_id,
      number: number_id,
      datein: datein || searchedDates.checkIn,
      dateout: dateout || searchedDates.checkOut,
    });
    toggleBookingWindow();
  };

  const openPhotoViewer = (images, index = 0) => {
    setPhotoViewerImages(images);
    setPhotoViewerIndex(index);
    setPhotoViewerOpen(true);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">⚠️ {error}</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка данных отеля...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Sprav Navigation */}
      <div className="sticky top-0 z-50" style={{ background: accentColor }}>
        {/* Mobile header */}
        <div
          className="sm:hidden px-2 py-1"
          style={{ background: accentColor }}
        >
          <div className="relative py-1">
            {cameFromSprav || cameFromMain ? (
              <>
                {/* Ссылка «Назад» + burger сверху */}
                <div className="flex items-center justify-between py-0.5">
                  {cameFromSprav ? (
                    <button
                      onClick={handleBackToResults}
                      className="flex items-center gap-1.5 text-white whitespace-nowrap transition-colors hover:text-white/80"
                      style={{
                        marginLeft: "4px",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>К результатам поиска</span>
                    </button>
                  ) : (
                    <button
                      onClick={handleBackToMain}
                      className="flex items-center gap-1.5 text-white whitespace-nowrap transition-colors hover:text-white/80"
                      style={{
                        marginLeft: "4px",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Назад, на главную</span>
                    </button>
                  )}
                  <button
                    onClick={() => setMobileBurgerOpen((v) => !v)}
                    className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full text-white hover:bg-white/15 transition-colors"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
                <HotelHeaderSearchField />
              </>
            ) : (
              /* Без ссылки: burger справа от поиска */
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <HotelHeaderSearchField />
                </div>
                <button
                  onClick={() => setMobileBurgerOpen((v) => !v)}
                  className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full text-white hover:bg-white/15 transition-colors"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile burger fullscreen overlay */}
        <AnimatePresence>
          {mobileBurgerOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileBurgerOpen(false)}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10009] sm:hidden"
              />
              <motion.div
                initial={{ y: "-100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                className="fixed top-0 left-0 right-0 shadow-2xl z-[10010] sm:hidden"
                style={{
                  background: `linear-gradient(to bottom right, ${accentColor}, ${accentColor}dd, ${accentColor}bb)`,
                }}
                ref={mobileBurgerRef}
              >
                <div className="flex flex-col">
                  <div className="flex items-center justify-between p-6 border-b border-white/10">
                    <button
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                        setMobileBurgerOpen(false);
                      }}
                      className="flex items-center gap-3"
                    >
                      <TreePine className="w-6 h-6 text-white" />
                      <h2 className="text-xl font-bold text-white">
                        {hotelData?.name}
                      </h2>
                    </button>
                    <button
                      onClick={() => setMobileBurgerOpen(false)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <X className="w-6 h-6 text-white" />
                    </button>
                  </div>
                  <nav className="p-4">
                    <div className="space-y-2">
                      {[
                        { label: "Бронировать", section: "rooms" },
                        { label: "О нас", section: "about" },
                        { label: "Как нас найти", section: "map" },
                        { label: "Контакты", section: "contacts" },
                      ].map((item) => (
                        <button
                          key={item.section}
                          onClick={() => {
                            scrollToSection(item.section);
                            setMobileBurgerOpen(false);
                          }}
                          className="w-full text-left px-4 py-3 text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-lg transition-all font-medium border border-white/20"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </nav>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Desktop header */}
        <div
          className="hidden sm:flex items-center gap-3 px-6 py-1"
          style={{ background: accentColor }}
        >
          {cameFromSprav && (
            <button
              onClick={handleBackToResults}
              className="flex items-center gap-1.5 text-white whitespace-nowrap transition-colors hover:text-white/80 flex-shrink-0"
              style={{ fontSize: "14px", fontWeight: 500 }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>К результатам поиска</span>
            </button>
          )}
          {cameFromMain && (
            <button
              onClick={handleBackToMain}
              className="flex items-center gap-1.5 text-white whitespace-nowrap transition-colors hover:text-white/80 flex-shrink-0"
              style={{ fontSize: "14px", fontWeight: 500 }}
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Назад, на главную</span>
            </button>
          )}
          <div className="w-[26rem] flex-shrink-0">
            <HotelHeaderSearchField />
          </div>
          {/* <div className="w-[26rem] flex-shrink-0">
            <HeaderSearchField
              page="hotel"
              initialDestination=""
              searchPlaceholder={hotelData?.name || ""}
              searchSubtitle={hotelData?.template || "Отель"}
            />
          </div> */}
          <nav className="flex items-center gap-1 flex-shrink-0 ml-auto">
            {[
              { label: "Бронировать", section: "rooms" },
              { label: "О нас", section: "about" },
              { label: "Как нас найти", section: "map" },
              { label: "Контакты", section: "contacts" },
            ].map((item) => (
              <button
                key={item.section}
                onClick={() => scrollToSection(item.section)}
                className="px-3 py-1.5 text-base font-semibold text-white/90 hover:text-white hover:bg-white/15 rounded-full transition-colors whitespace-nowrap"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <HeroSection
        hotelData={hotelData}
        accentColor={accentColor}
        onPhotoViewerOpen={openPhotoViewer}
      />

      <AmenitiesSection
        popular_services={hotelData?.popular_services || []}
        accentColor={accentColor}
      />

      {/* Rooms Section */}
      <section
        id="rooms"
        className="py-20 bg-gradient-to-b from-gray-50 to-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span
                className="inline-block px-4 py-1.5 text-sm font-medium rounded-full mb-4"
                style={{
                  backgroundColor: `${accentColor}20`,
                  color: accentColor,
                }}
              >
                Наши номера
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                Забронируй идеальное место
              </h2>
              <p className="text-lg text-gray-600">
                Каждый номер оборудован всем необходимым для комфортного отдыха
              </p>
            </motion.div>
            <div className="hidden md:flex gap-2 mt-4 sm:mt-0">
              <Button
                variant={roomView === "grid" ? "default" : "outline"}
                size="icon"
                onClick={() => setRoomView("grid")}
                style={
                  roomView === "grid"
                    ? { backgroundColor: accentColor, borderColor: accentColor }
                    : {}
                }
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={roomView === "list" ? "default" : "outline"}
                size="icon"
                onClick={() => setRoomView("list")}
                style={
                  roomView === "list"
                    ? { backgroundColor: accentColor, borderColor: accentColor }
                    : {}
                }
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Mobile only (< md): vertical cards grid — gap-6 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:hidden">
            {combinedRooms.length > 0 &&
              combinedRooms.map((room) => (
                <RoomCardVertical
                  key={room.id}
                  room={room}
                  roomPhotoUrl={hotelData.photo_url}
                  photoIndex={roomPhotoIndices[room.id] || 0}
                  slideDirection={roomSlideDirections[room.id] || 1}
                  onPrev={() => prevRoomPhoto(room.id)}
                  onNext={() => nextRoomPhoto(room.id)}
                  onPhotoClick={openPhotoViewer}
                  expandedFeatures={expandedFeatures[`m-${room.id}`]}
                  onToggleFeatures={() =>
                    setExpandedFeatures((prev) => ({
                      ...prev,
                      [`m-${room.id}`]: !prev[`m-${room.id}`],
                    }))
                  }
                  onShowDescription={() => setShowDescription(room)}
                  onBooking={bookingHandler}
                  onBook={() => openBookingDialog(room)}
                  datesSelected={datesSelected}
                  nights={calcNights(
                    searchedDates.checkIn,
                    searchedDates.checkOut,
                  )}
                  featureKey={`m-${room.id}`}
                  onDesignVariants={() => setDesignVariantsOpen(room)}
                  accentColor={accentColor}
                />
              ))}
          </div>

          {/* md+: grid or list */}
          {roomView === "grid" ? (
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {combinedRooms.length > 0 &&
                combinedRooms.map((room) => (
                  <RoomCardVertical
                    key={room.id}
                    room={room}
                    roomPhotoUrl={hotelData.photo_url}
                    photoIndex={roomPhotoIndices[room.id] || 0}
                    slideDirection={roomSlideDirections[room.id] || 1}
                    onPrev={() => prevRoomPhoto(room.id)}
                    onNext={() => nextRoomPhoto(room.id)}
                    onPhotoClick={openPhotoViewer}
                    expandedFeatures={expandedFeatures[`g-${room.id}`]}
                    onToggleFeatures={() =>
                      setExpandedFeatures((prev) => ({
                        ...prev,
                        [`g-${room.id}`]: !prev[`g-${room.id}`],
                      }))
                    }
                    onShowDescription={() => setShowDescription(room)}
                    onBooking={bookingHandler}
                    onBook={() => openBookingDialog(room)}
                    datesSelected={datesSelected}
                    nights={calcNights(
                      searchedDates.checkIn,
                      searchedDates.checkOut,
                    )}
                    featureKey={`g-${room.id}`}
                    onDesignVariants={() => setDesignVariantsOpen(room)}
                    accentColor={accentColor}
                  />
                ))}
            </div>
          ) : (
            <div className="hidden md:block space-y-4">
              {combinedRooms.length > 0 &&
                combinedRooms.map((room, idx) => (
                  <RoomCardHorizontal
                    key={room.id}
                    room={room}
                    roomPhotoUrl={hotelData.photo_url}
                    photoIndex={roomPhotoIndices[room.id] || 0}
                    slideDirection={roomSlideDirections[room.id] || 1}
                    onPrev={() => prevRoomPhoto(room.id)}
                    onNext={() => nextRoomPhoto(room.id)}
                    onPhotoClick={openPhotoViewer}
                    expandedFeatures={expandedFeatures[`g-${room.id}`]}
                    onToggleFeatures={() =>
                      setExpandedFeatures((prev) => ({
                        ...prev,
                        [`g-${room.id}`]: !prev[`g-${room.id}`],
                      }))
                    }
                    onShowDescription={() => setShowDescription(room)}
                    onBooking={bookingHandler}
                    onBook={() => openBookingDialog(room)}
                    datesSelected={datesSelected}
                    nights={calcNights(
                      searchedDates.checkIn,
                      searchedDates.checkOut,
                    )}
                    featureKey={`g-${room.id}`}
                    onDesignVariants={() => setDesignVariantsOpen(room)}
                    accentColor={accentColor}
                  />
                ))}
            </div>
          )}
        </div>
      </section>

      <div id="about">
        <AboutSection
          hotel={hotelData}
          hotelImages={hotelImages}
          photosPositions={hotelData?.photos_positions || {}}
          openPhotoViewer={openPhotoViewer}
          onShowRules={() => setShowRules(true)}
        />
      </div>

      <LivingConditionsSection hotel={hotelData} />

      <div id="map">
        <MapSection
          coordinates={hotelData?.coordinates || []}
          hotel_name={hotelData?.name}
          map_points={hotelData?.map_points || null}
          accentColor={accentColor}
        />
      </div>

      <div id="contacts">
        <ContactSection hotel={hotelData} accentColor={accentColor} />
      </div>

      {/* Room Description Dialog */}
      <Dialog open={showDescription} onOpenChange={setShowDescription}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Подробное описание номера
            </DialogTitle>
          </DialogHeader>
          <div
            className="text-gray-700 space-y-4 text-base leading-none mt-2 mb-5"
            style={{ whiteSpace: "pre-line" }}
          >
            {showDescription?.description}
          </div>
          <div className="border-t pt-5 mt-2">
            <h3 className="text-lg font-bold text-gray-900 mb-5">
              Услуги и удобства
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="space-y-5">
                {showDescription?.number_of_rooms && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Home className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Количество комнат
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <li>• {showDescription.number_of_rooms}</li>
                    </ul>
                  </div>
                )}

                {showDescription?.room_capacity && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Bed className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Количество спальных мест
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <li>• {showDescription.room_capacity}</li>
                    </ul>
                  </div>
                )}

                {showDescription?.hotels_space && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Площадь номера
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <li>• {showDescription.hotels_space} м²</li>
                    </ul>
                  </div>
                )}

                {showDescription?.hotels_view?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Eye className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">Вид</h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <PrintListLanding
                        id="view"
                        list={showDescription.hotels_view}
                      />
                    </ul>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {showDescription?.hotels_equip?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Tv className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Техника в номере
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <PrintListLanding
                        id="equip"
                        list={showDescription.hotels_equip}
                      />
                    </ul>
                  </div>
                )}

                {showDescription?.hotels_wc_additional?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Bath className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Оборудование санузла
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <PrintListLanding
                        id="wc"
                        list={showDescription.hotels_wc_additional}
                      />
                    </ul>
                  </div>
                )}

                {showDescription?.hotel_wc_location?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <DoorOpen className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Расположение санузла
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <PrintListLanding
                        id="wc_loc"
                        list={showDescription.hotel_wc_location}
                      />
                    </ul>
                  </div>
                )}
              </div>

              <div className="space-y-5">
                {showDescription?.hotels_equip_additional?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">
                        Дополнительные преимущества
                      </h4>
                    </div>
                    <ul className="space-y-1.5 text-sm text-gray-600">
                      <PrintListLanding
                        id="additional"
                        list={showDescription.hotels_equip_additional}
                      />
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RoomBookingDialog
        open={bookingDialogOpen}
        onOpenChange={setBookingDialogOpen}
        onBooking={bookingHandler}
        room={selectedRoom}
        accentColor={accentColor}
        hotelData={hotelData}
        bookingData={bookingData}
        toggleBookingWindow={toggleBookingWindow}
      />

      <DesignVariantsDialog
        open={designVariantsOpen}
        room={designVariantsOpen}
        hotelData={hotelData}
        roomPhotoUrl={hotelData.photo_url}
        onOpenChange={setDesignVariantsOpen}
        onBooking={bookingHandler}
        accentColor={accentColor}
      />

      <PhotoViewer
        images={photoViewerImages}
        initialIndex={photoViewerIndex}
        open={photoViewerOpen}
        onOpenChange={setPhotoViewerOpen}
      />

      {/* Rules Dialog */}
      <Dialog open={showRules} onOpenChange={setShowRules}>
        <DialogContent className="max-w-3xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Правила проживания
            </DialogTitle>
          </DialogHeader>
          <div className="text-gray-700 leading-relaxed space-y-6 text-sm whitespace-pre-line">
            {hotelData?.rules_of_residence || ""}
            <div className="pt-4 border-t">
              <p className="text-gray-600 text-xs">
                Проживая в {hotelDeclensions?.[hotelData?.template] || "отеле"}{" "}
                «{hotelData?.name || hotelData?.id}», вы автоматически
                соглашаетесь с данными правилами. Администрация оставляет за
                собой право вносить изменения в правила проживания.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showBookingWindow && (
        <div className="bookingWindow">
          <MainPopup
            onClose={toggleBookingWindow}
            classnames={{ bookingPopup: 1 }}
          >
            <div style={{ maxHeight: "80vh", minHeight: "15vh" }}>
              <BookingWindow
                bookingData={bookingData}
                onClose={toggleBookingWindow}
              />
            </div>
          </MainPopup>
        </div>
      )}
    </div>
  );
}
